import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Phone, MessageCircle, MapPin, Clock, CalendarDays } from "lucide-react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import MenuItemCard from "../components/common/MenuItemCard";
import { StarRating, Spinner, EmptyState } from "../components/common/UI";
import toast from "react-hot-toast";

export default function RestaurantPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { cart, totalItems, totalPrice } = useCart();
  const [restaurant, setRestaurant] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [tab, setTab] = useState("menu");
  const [reviewForm, setReviewForm] = useState({ rating: 0, comment: "" });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [userCanReview, setUserCanReview] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data: rest } = await supabase
        .from("restaurants")
        .select("*")
        .eq("slug", slug)
        .single();
      if (!rest) { setLoading(false); return; }
      setRestaurant(rest);

      const [{ data: items }, { data: revs }] = await Promise.all([
        supabase.from("menu_items").select("*").eq("restaurant_id", rest.id).eq("is_available", true).order("category"),
        supabase.from("reviews").select("*, users(name)").eq("restaurant_id", rest.id).order("created_at", { ascending: false }),
      ]);
      setMenuItems(items || []);
      setReviews(revs || []);

      if (user) {
        const { count } = await supabase
          .from("orders")
          .select("*", { count: "exact", head: true })
          .eq("user_id", user.id)
          .eq("restaurant_id", rest.id)
          .eq("status", "delivered");
        setUserCanReview((count || 0) > 0);
      }
      setLoading(false);
    };
    load();
  }, [slug, user]);

  const categories = ["All", ...new Set(menuItems.map((i) => i.category).filter(Boolean))];
  const filteredItems = activeCategory === "All" ? menuItems : menuItems.filter((i) => i.category === activeCategory);

  const handleCall = () => window.open(`tel:${restaurant.contact}`);
  const handleWhatsApp = () => {
    const num = (restaurant.whatsapp || restaurant.contact).replace(/\D/g, "");
    window.open(`https://wa.me/${num}`);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.rating) { toast.error("Please select a rating"); return; }
    setSubmittingReview(true);
    const { error } = await supabase.from("reviews").insert({
      user_id: user.id,
      restaurant_id: restaurant.id,
      rating: reviewForm.rating,
      comment: reviewForm.comment,
    });
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Review submitted!");
      setReviewForm({ rating: 0, comment: "" });
      const { data: revs } = await supabase
        .from("reviews")
        .select("*, users(name)")
        .eq("restaurant_id", restaurant.id)
        .order("created_at", { ascending: false });
      setReviews(revs || []);
    }
    setSubmittingReview(false);
  };

  if (loading) return (
    <>
      <Navbar />
      <div className="flex justify-center py-24"><Spinner size="lg" /></div>
    </>
  );

  if (!restaurant) return (
    <>
      <Navbar />
      <EmptyState icon="🔍" title="Restaurant not found" description="This restaurant doesn't exist or has been removed." action={<Link to="/restaurants" className="btn-primary">Browse Restaurants</Link>} />
    </>
  );

  const isCartFromHere = cart.restaurantId === restaurant.id;

  return (
    <>
      <Helmet>
        <title>{restaurant.name} — Khana Spot</title>
        <meta name="description" content={restaurant.description || `Order from ${restaurant.name} in ${restaurant.location}`} />
        <meta property="og:title" content={`${restaurant.name} — Khana Spot`} />
        <meta property="og:description" content={restaurant.description} />
        {restaurant.cover_url && <meta property="og:image" content={restaurant.cover_url} />}
        <link rel="canonical" href={`${window.location.origin}/restaurant/${slug}`} />
      </Helmet>
      <Navbar />

      {/* Cover */}
      <div className="relative h-52 sm:h-72 bg-gradient-to-br from-orange-200 to-amber-200 overflow-hidden">
        {restaurant.cover_url ? (
          <img src={restaurant.cover_url} alt={restaurant.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-8xl opacity-20">🍽️</div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
          <div className="max-w-7xl mx-auto flex items-end gap-4">
            {restaurant.logo_url && (
              <img src={restaurant.logo_url} alt={restaurant.name} className="w-20 h-20 rounded-2xl border-4 border-white shadow-lg object-cover flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-2xl sm:text-3xl font-bold drop-shadow">{restaurant.name}</h1>
              <p className="text-orange-200 text-sm mt-0.5">{restaurant.cuisine_type?.join(" · ")}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Info bar */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 -mt-4 relative z-10 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-1.5 text-sm text-gray-600">
              <MapPin className="w-4 h-4 text-orange-400" /> {restaurant.location}
            </div>
            <div className="flex items-center gap-1.5 text-sm text-gray-600">
              <Clock className="w-4 h-4 text-orange-400" /> {restaurant.opening_time} – {restaurant.closing_time}
            </div>
            <div className="flex items-center gap-1.5 text-sm">
              <span className="star text-base">★</span>
              <span className="font-semibold">{restaurant.rating > 0 ? restaurant.rating.toFixed(1) : "New"}</span>
              <span className="text-gray-400">({restaurant.review_count} reviews)</span>
            </div>
            <span className={`badge ml-auto ${restaurant.is_open ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
              {restaurant.is_open ? "● Open Now" : "● Closed"}
            </span>
          </div>
          {restaurant.description && (
            <p className="text-gray-600 text-sm mt-3 leading-relaxed">{restaurant.description}</p>
          )}
          <div className="flex flex-wrap gap-2 mt-4">
            <button onClick={handleCall} className="flex items-center gap-2 border border-gray-200 hover:border-orange-400 text-gray-700 hover:text-orange-600 px-4 py-2 rounded-xl text-sm font-medium transition-colors">
              <Phone className="w-4 h-4" /> Call Restaurant
            </button>
            <button onClick={handleWhatsApp} className="flex items-center gap-2 bg-green-50 border border-green-200 hover:bg-green-100 text-green-700 px-4 py-2 rounded-xl text-sm font-medium transition-colors">
              <MessageCircle className="w-4 h-4" /> Order on WhatsApp
            </button>
            <Link to={`/restaurant/${slug}/book`} className="flex items-center gap-2 bg-orange-50 border border-orange-200 hover:bg-orange-100 text-orange-700 px-4 py-2 rounded-xl text-sm font-medium transition-colors">
              <CalendarDays className="w-4 h-4" /> Book a Table
            </Link>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          {["menu", "reviews"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-5 py-3 text-sm font-semibold capitalize border-b-2 transition-colors ${
                tab === t ? "border-orange-500 text-orange-600" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {t === "menu" ? `🍽️ Menu (${menuItems.length})` : `⭐ Reviews (${reviews.length})`}
            </button>
          ))}
        </div>

        {tab === "menu" && (
          <div className="pb-32">
            {/* Category filter */}
            {categories.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-3 mb-5">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                      activeCategory === cat
                        ? "bg-orange-500 text-white border-orange-500"
                        : "border-gray-200 text-gray-600 hover:border-orange-300"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            {filteredItems.length === 0 ? (
              <EmptyState icon="🍳" title="No items yet" description="This restaurant hasn't added menu items yet." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {filteredItems.map((item) => (
                  <MenuItemCard key={item.id} item={item} restaurantId={restaurant.id} restaurantName={restaurant.name} />
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "reviews" && (
          <div className="pb-12 space-y-6">
            {/* Rating summary */}
            <div className="bg-orange-50 rounded-2xl p-5 flex items-center gap-6">
              <div className="text-center">
                <div className="font-display text-5xl font-black text-orange-500">{restaurant.rating > 0 ? restaurant.rating.toFixed(1) : "—"}</div>
                <div className="flex mt-1">{[1,2,3,4,5].map(s => <span key={s} className={s <= Math.round(restaurant.rating) ? "star" : "star-empty"}>★</span>)}</div>
                <div className="text-xs text-gray-500 mt-1">{restaurant.review_count} reviews</div>
              </div>
            </div>

            {/* Write review */}
            {user && userCanReview && (
              <div className="bg-white border border-gray-200 rounded-2xl p-5">
                <h3 className="font-semibold text-gray-900 mb-4">Write a Review</h3>
                <form onSubmit={submitReview} className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-600 mb-2 block">Your Rating</label>
                    <StarRating rating={reviewForm.rating} onChange={(r) => setReviewForm((f) => ({ ...f, rating: r }))} size="lg" />
                  </div>
                  <textarea
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm((f) => ({ ...f, comment: e.target.value }))}
                    placeholder="Share your experience..."
                    rows={3}
                    className="input-field resize-none"
                  />
                  <button type="submit" disabled={submittingReview} className="btn-primary">
                    {submittingReview ? "Submitting..." : "Submit Review"}
                  </button>
                </form>
              </div>
            )}
            {user && !userCanReview && (
              <p className="text-sm text-gray-500 text-center py-4">
                ℹ️ You can write a review after completing an order from this restaurant.
              </p>
            )}

            {/* Reviews list */}
            {reviews.length === 0 ? (
              <EmptyState icon="⭐" title="No reviews yet" description="Be the first to review this restaurant!" />
            ) : (
              reviews.map((r) => (
                <div key={r.id} className="bg-white border border-gray-100 rounded-2xl p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-orange-100 rounded-full flex items-center justify-center font-bold text-orange-600 text-sm">
                        {(r.users?.name || "U")[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-gray-900">{r.users?.name || "Anonymous"}</p>
                        <div className="flex mt-0.5">{[1,2,3,4,5].map(s => <span key={s} className={`text-xs ${s <= r.rating ? "star" : "star-empty"}`}>★</span>)}</div>
                      </div>
                    </div>
                    <span className="text-xs text-gray-400">{new Date(r.created_at).toLocaleDateString("en-PK")}</span>
                  </div>
                  {r.comment && <p className="text-sm text-gray-600 mt-3 leading-relaxed">{r.comment}</p>}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Sticky cart bar */}
      {isCartFromHere && totalItems > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 p-4 bg-white/95 backdrop-blur border-t border-gray-200 shadow-2xl">
          <div className="max-w-xl mx-auto">
            <Link to="/checkout" className="flex items-center justify-between btn-primary w-full">
              <div className="flex items-center gap-3">
                <span className="bg-white/20 rounded-lg px-2 py-0.5 font-bold">{totalItems}</span>
                <span>View Cart</span>
              </div>
              <span className="font-bold">₨{totalPrice.toLocaleString()} →</span>
            </Link>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}
